import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import {
    ColDef as BaseColDef,
    ModuleRegistry,
    RowSelectedEvent,
    RowStyleModule,
    SelectionChangedEvent,
    GridOptions,
    RowSelectionModule,
} from "ag-grid-community";

interface ColDef extends BaseColDef {
    showNist?: boolean;
}
import { ClientSideRowModelModule } from "ag-grid-community";
import { PaginationModule } from "ag-grid-community";
import {
    Box,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    useTheme,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AppsIcon from "@mui/icons-material/Apps";
import FilterListIcon from "@mui/icons-material/FilterList";
import ReactDOMServer from "react-dom/server";

import {
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
} from "ag-grid-community";
import { SkeletonLoader } from "../../modules/dashboard/shared";
import NoRowsOverlay from "./NoRowsOverlay";

// Register required modules
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    PaginationModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    RowStyleModule,
    RowSelectionModule,
]);

interface AgGridTableProps {
    columnDefs: ColDef[];
    data: any[];
    showSearch?: boolean;
    searchPlaceholder?: string;
    height?: number | string;
    width?: number;
    rowSelection?: "single" | "multiple";
    isRowSelectable?: boolean;
    headerActions?: React.ReactNode;
    onRowClick?: (rowData: any) => void;
    showAppsIcon?: boolean;
    pagination?: boolean;
    paginationPageSize?: number;
    gridOptions?: GridOptions;
    styles?: any;
    isLoading?: boolean;
    onSelectionChange?: (selectedRows: any[]) => void;
    checkboxSelection?: boolean;
    headerCheckboxSelection?: boolean;
    headerCheckboxSelectionCurrentPageOnly?: boolean;
    gridRef?: React.Ref<any>;
    activeFilters?: Record<string, string[]>; // 🚀 Generic filter prop
    onSearchNoResults?: (searchText: string) => void; // Callback when search yields no results
    onSearchClear?: () => void; // Callback when search is cleared (to reset/refetch data)
    onSearchSubmit?: (searchText: string) => void; // Callback when search is submitted (Enter pressed)
    onFilteredCountChange?: (count: number) => void; // Callback when local filtering changes the row count
    scrollDelay?: number; // Delay in ms before scroll is enabled (prevents scroll hijacking)
    mandatoryColumns?: string[]; // Array of column headerNames that cannot be hidden
    hasActiveAgent?: boolean;
    onAddRuleClick?: () => void;
    noRowsMessage?: string;
    onSearchChange?: (searchText: string) => void; // 🚀 New callback for search text
    disableLocalSearch?: boolean; // 🚀 Disables local filtering if parent handles it
}

const AgGridTable: React.FC<AgGridTableProps> = ({
    columnDefs,
    data,
    showSearch,
    height = 50,
    width,
    isRowSelectable = false,
    headerActions,
    onRowClick,
    showAppsIcon = true,
    pagination = true,
    paginationPageSize = 50,
    gridOptions,
    styles,
    isLoading,
    onSelectionChange,
    searchPlaceholder,
    checkboxSelection = false,
    headerCheckboxSelection = false,
    headerCheckboxSelectionCurrentPageOnly = false,
    gridRef,
    activeFilters = {}, // 🚀 Generic filter prop
    onSearchNoResults, // Callback when search yields no results
    onSearchClear, // Callback when search is cleared
    onSearchSubmit, // Callback when search is submitted
    onFilteredCountChange, // Callback when local filtering changes
    scrollDelay = 0, // Default: no delay
    mandatoryColumns = [], // Default: no mandatory columns
    hasActiveAgent,
    onAddRuleClick,
    noRowsMessage,
    onSearchChange,
    disableLocalSearch = false,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isCloudScan = window.location.pathname.includes('cloud/scans');
    const isOnPremScan = window.location.pathname.includes('on-premises/scans');
    const isAsset = window.location.pathname.includes("on-premises/assets");
    const isCloudResource = window.location.pathname.includes("cloud/cloud-resources");

    const shouldShowHeaderWhenEmpty = isCloudScan || isOnPremScan || isAsset || isCloudResource;

    // 🎯 SCROLL DELAY: State and timer for preventing scroll hijacking
    const [isScrollEnabled, setIsScrollEnabled] = useState(scrollDelay === 0);
    const scrollDelayTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Mouse enter handler - start timer to enable scroll after delay
    const handleMouseEnter = useCallback(() => {
        if (scrollDelay > 0 && !isScrollEnabled) {
            scrollDelayTimerRef.current = setTimeout(() => {
                setIsScrollEnabled(true);
            }, scrollDelay);
        }
    }, [scrollDelay, isScrollEnabled]);

    // Mouse leave handler - cancel timer and disable scroll
    const handleMouseLeave = useCallback(() => {
        if (scrollDelayTimerRef.current) {
            clearTimeout(scrollDelayTimerRef.current);
            scrollDelayTimerRef.current = null;
        }
        if (scrollDelay > 0) {
            setIsScrollEnabled(false);
        }
    }, [scrollDelay]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (scrollDelayTimerRef.current) {
                clearTimeout(scrollDelayTimerRef.current);
            }
        };
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);

    // Custom Ctrl+C handling to allow copying cell values in Community Edition
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                // If the user has text selected natively, don't interfere
                if (window.getSelection()?.toString()) return;
                
                // Otherwise, copy the focused cell's value
                if (gridRef && typeof gridRef !== 'function' && gridRef.current) {
                    const api = (gridRef.current as any).api;
                    const focusedCell = api?.getFocusedCell();
                    if (focusedCell) {
                        const rowNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
                        if (rowNode) {
                            const value = api.getValue(focusedCell.column, rowNode);
                            if (value) {
                                navigator.clipboard.writeText(String(value));
                            }
                        }
                    }
                }
            }
        };

        const el = containerRef.current;
        if (el) {
            el.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            if (el) {
                el.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [gridRef]);

    const [visibleColumns, setVisibleColumns] = useState<string[]>(
        columnDefs
            ?.filter((col) => col.hide !== true) // Exclude columns with hide: true
            ?.map((col) => col?.field ?? "")
    );

    useEffect(() => {
        setVisibleColumns(
            columnDefs
                .filter((col) => col?.showNist === undefined || col?.showNist !== false) // Include columns where showNist is undefined or not false
                .filter((col) => col.hide !== true) // Exclude columns with hide: true
                .map((col) => col?.field ?? "") // Map remaining columns to their field names
        );

    }, [columnDefs]);

    const customIcons = useMemo(() => ({
        filter: ReactDOMServer.renderToString(
            <FilterListIcon
                style={{
                    fontSize: 16,
                    color: theme.palette.mode === 'dark' ? '#F8F8FF' : '#666',
                    opacity: 0.7
                }}
            />
        ),
    }), [theme.palette.mode]);



    const [searchText, setSearchText] = useState("");
    const [shouldTriggerServerSearch, setShouldTriggerServerSearch] = useState(false); // Only true when Enter is pressed
    const headerAreaHeight = useMemo(() => {
        const hasHeaderBar = Boolean(showSearch || headerActions || showAppsIcon);
        return hasHeaderBar ? 40 : 0; // reserve space for search/actions row
    }, [showSearch, headerActions, showAppsIcon]);

    const agGridInlineStyles = useMemo(
        () => ({
            padding: "0px",
            // "& .ag-body-vertical-scroll": {
            //     width: "0px !important",
            //     minWidth: "0px !important",
            // },
            // "& .ag-body-vertical-scroll-viewport": {
            //     width: "0px !important",
            //     minWidth: "0px !important",
            // },
            "& .ag-center-cols-clipper": {
                marginRight: 0,
            },
            "& .ag-center-cols-viewport": {
                marginRight: 0,
            },
            "& img, & svg": {
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
            },
        }),
        []
    );

    const onRowClicked = useCallback(
        (event: any) => {
            const targetElement = event.event.target as HTMLElement;

            // Check if the clicked element or any parent has data-prevent-row-click attribute
            if (targetElement.closest('[data-prevent-row-click="true"]')) {
                return;
            }

            if (targetElement.closest("button") || targetElement.closest(".ag-column-actions")) {
                event.event.stopPropagation();
                return;
            }
            if (targetElement.classList.contains("ag-criticality-bar")) {
                event.event.stopPropagation();
                return;
            }
            // Prevent row click for action columns like "showDiff" and "addToCompare"
            // Try multiple ways to get the column ID
            const columnId = event.column?.getColId?.() || event.column?.colId || event.colDef?.field;
            if (columnId === "showDiff" || columnId === "addToCompare" || columnId === "vector_string") {
                return;
            }
            // Also check by looking at the cell's data-col-id attribute or the closest cell
            const cell = targetElement.closest('.ag-cell') as HTMLElement | null;
            if (cell) {
                const colId = cell.getAttribute('col-id');
                if (colId === "showDiff" || colId === "addToCompare" || colId === "vector_string") {
                    return;
                }
            }
            // If the click is on an IconButton or its children, prevent row click
            if (targetElement.closest('.MuiIconButton-root')) {
                event.event.stopPropagation();
                return;
            }
            if (onRowClick) {
                onRowClick(event.data);
            }
        },
        [onRowClick]
    );

    const valueMatches = (value: any, needle: string): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'object') {
            // Search nested objects/arrays
            if (Array.isArray(value)) {
                return value.some((v) => valueMatches(v, needle));
            }
            return Object.values(value).some((v) => valueMatches(v, needle));
        }
        return value.toString().toLowerCase().includes(needle);
    };

    const filteredData = useMemo(() => {
        // Handle null/undefined data
        if (!data) return [];

        let filtered = data;

        // 🚀 Generic filtering using activeFilters
        Object.entries(activeFilters).forEach(([fieldName, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                console.log(`🚀 AgGridTable filtering by ${fieldName}:`, selectedValues);
                filtered = filtered.filter((row) =>
                    selectedValues.includes(row[fieldName])
                );
                console.log(`🚀 AgGridTable filtered count after ${fieldName}:`, filtered.length);
            }
        });

        // Finally filter by search text
        if (searchText && searchText.trim() && !disableLocalSearch) {
            const normalizedSearch = searchText.toString().toLowerCase().trim();
            filtered = filtered.filter((row) => valueMatches(row, normalizedSearch));
            console.log('🚀 AgGridTable filtered count after search:', filtered.length);
        }

        return filtered;
    }, [searchText, data, activeFilters]);

    // Track the last search text that triggered server search to prevent duplicate calls
    const lastServerSearchRef = useRef<string>("");

    // Notify parent of filtered count changes
    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(filteredData.length);
        }
    }, [filteredData.length, onFilteredCountChange]);

    // 🚀 Notify parent of search text changes
    useEffect(() => {
        if (onSearchChange) {
            onSearchChange(searchText);
        }
    }, [searchText, onSearchChange]);

    // Call onSearchNoResults when search yields no results (only when Enter is pressed)
    useEffect(() => {
        // Only trigger server search if:
        // 1. User pressed Enter (shouldTriggerServerSearch is true)
        // 2. There's a search text
        // 3. No local results found
        // 4. There's data loaded (to avoid triggering on initial empty state)
        // 5. We haven't already searched for this exact text on the server
        // 6. The callback exists
        if (
            shouldTriggerServerSearch &&
            searchText &&
            lastServerSearchRef.current !== searchText
        ) {
            if (onSearchSubmit) {
                console.log('🔍 Triggering onSearchSubmit for:', searchText);
                lastServerSearchRef.current = searchText;
                onSearchSubmit(searchText);
            } else if (
                filteredData && filteredData.length === 0 &&
                data && data.length > 0 &&
                onSearchNoResults
            ) {
                console.log('🔍 No local results found for search, triggering server search:', searchText);
                lastServerSearchRef.current = searchText; // Mark this search as triggered
                onSearchNoResults(searchText);
            }
        }
        // Reset the flag after processing
        if (shouldTriggerServerSearch) {
            setShouldTriggerServerSearch(false);
        }

        // When search is cleared and we had made a server search, reset and refetch original data
        if (!searchText && lastServerSearchRef.current !== "" && onSearchClear) {
            console.log('🔄 Search cleared, resetting to original data');
            lastServerSearchRef.current = "";
            onSearchClear();
        } else if (!searchText) {
            lastServerSearchRef.current = "";
        }
    }, [searchText, filteredData, data, onSearchNoResults, onSearchClear, shouldTriggerServerSearch]);

    // const filteredData = useMemo(() => {
    //     if (!searchText) return data;

    //     const normalizedSearch = searchText.toString().toLowerCase();

    //     return data.filter((row) =>
    //         Object.values(row).some((value) => {
    //             if (value === null || value === undefined) return false;
    //             return value.toString().toLowerCase() === normalizedSearch;
    //         })
    //     );
    // }, [searchText, data]);



    const onRowSelected = useCallback((event: RowSelectedEvent) => {
        if (!isRowSelectable) return;
        console.log(
            "row " +
            event.node.data.athlete +
            " selected = " +
            event.node.isSelected()
        );
    }, [isRowSelectable]);

    const handleColumnToggle = (field: string) => {
        // Find the column definition to check if it's a mandatory column
        const columnDef = columnDefs.find(col => col.field === field);

        // Prevent toggling off mandatory columns
        if (columnDef?.headerName && mandatoryColumns.includes(columnDef.headerName)) {
            return;
        }

        // Prevent toggling off the "#" (number) column - it must always be visible
        if (field === 'number') {
            return;
        }

        setVisibleColumns((prev) => {
            const newVisibleColumns = prev.includes(field)
                ? prev.filter((col) => col !== field)
                : [...prev, field];

            // Check if only the "number" column is visible (excluding selection and actions)
            const regularColumns = columnDefs.filter(
                col => col.field !== 'selection' && col.field !== 'actions' && col.headerName
            );
            const visibleRegularColumns = newVisibleColumns.filter(
                col => regularColumns.some(regCol => regCol.field === col)
            );

            // If only "number" column is visible, hide selection and actions
            if (visibleRegularColumns.length === 1 && visibleRegularColumns[0] === 'number') {
                return newVisibleColumns.filter(col => col !== 'selection' && col !== 'actions');
            }

            // If more than just "number" column is visible, ensure selection is visible
            const hasSelection = columnDefs.some(col => col.field === 'selection');

            let result = [...newVisibleColumns];
            if (hasSelection && !result.includes('selection')) {
                result.push('selection');
            }

            return result;
        });
    };

    const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
        if (!isRowSelectable) return;
        const selectedNodes = event.api.getSelectedNodes();
        const selectedData = selectedNodes.map((node: any) => node.data);
        onSelectionChange?.(selectedData);
    }, [isRowSelectable, onSelectionChange]);

    const rowSelection = useMemo(() => {
        return isRowSelectable ? "multiple" : undefined;
    }, [isRowSelectable]);

    const getRowClass = (params: any) => {
        return params.node.rowIndex % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
    };

    const processedColumnDefs = useMemo(() => {
        return columnDefs?.map((col, index) => ({
            ...col,
            hide: col?.field ? !visibleColumns.includes(col.field) : false,
            checkboxSelection: col.checkboxSelection ?? (index === 0 && checkboxSelection),
            headerCheckboxSelection: col.headerCheckboxSelection ?? (index === 0 && headerCheckboxSelection),
            headerCheckboxSelectionCurrentPageOnly: col.headerCheckboxSelectionCurrentPageOnly ?? (index === 0 && headerCheckboxSelectionCurrentPageOnly),
            icons: {
                filter: ReactDOMServer.renderToString(
                    <FilterListIcon
                        style={{
                            fontSize: 16,
                            color: theme.palette.mode === 'dark' ? '#fff' : '#666',
                            opacity: 0.7
                        }}
                    />
                ),
            },
        }));
    }, [columnDefs, visibleColumns, checkboxSelection, headerCheckboxSelection, headerCheckboxSelectionCurrentPageOnly, theme.palette.mode]);

    const mergedGridOptions: GridOptions = useMemo(() => {
        return {
            ...gridOptions,
            domLayout: gridOptions?.domLayout !== undefined ? gridOptions.domLayout : (!pagination ? 'autoHeight' : undefined),
            rowSelection: rowSelection as any,
            onRowSelected,
            onSelectionChanged: handleSelectionChanged,
            getRowClass,
            onRowClicked,
            // enableCellTextSelection: true,
            // ensureDomOrder: true,

            defaultColDef: {
                flex: 1,
                sortable: true,
                filter: true,
                resizable: true,
                icons: customIcons,
                menuTabs: ['filterMenuTab'],
                floatingFilter: false,
                ...(gridOptions?.defaultColDef || {}),
            },

            suppressRowClickSelection: true,
            suppressCellFocus: false,
            rowMultiSelectWithClick: false,
            suppressRowHoverHighlight: false,
            suppressRowTransform: true,
            rowClass: "ag-row-no-highlight",
            pagination,
            paginationPageSize,
            paginationPageSizeSelector: gridOptions?.paginationPageSizeSelector ?? [10, 20, 50, 100],
            headerHeight: 32,
        };
    }, [gridOptions, columnDefs, checkboxSelection, headerCheckboxSelection, rowSelection, onRowSelected, handleSelectionChanged, onRowClicked, pagination, paginationPageSize, customIcons]);

    return (
        <Box
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                width: "100%",
                height: height,
                fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                ...styles,
                // 🎯 SCROLL DELAY: Disable scroll on AG Grid viewport when scroll is not enabled
                ...(!isScrollEnabled && scrollDelay > 0 && {
                    "& .ag-body-viewport": {
                        overflow: "hidden !important",
                        pointerEvents: "none",
                    },
                    "& .ag-body-horizontal-scroll-viewport": {
                        overflow: "hidden !important",
                        pointerEvents: "none",
                    },
                    "& .ag-body-vertical-scroll-viewport": {
                        overflow: "hidden !important",
                        pointerEvents: "none",
                    },
                }),
                "& .ag-theme-material": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-header": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-header-cell": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-row": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
                    transition: "background-color 0.2s ease",
                    cursor: onRowClick ? "pointer" : "default",
                    "&:hover": {
                        backgroundColor: `${theme.palette.action.hover} !important`,
                    }
                },
                "& .ag-paging-panel": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-paging-row-summary-panel": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-paging-page-summary-panel": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                },
                "& .ag-theme-material .ag-row-even": {
                    backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05) !important"
                        : "#F8F8FF !important"
                },
                "& .ag-theme-material .ag-row-odd": {
                    backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.2) !important"
                        : "#FFFFFF !important"
                },
                "& .ag-cell": {
                    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
                    transition: "background-color 0.2s ease",
                    userSelect: "text",
                    "&:hover": {
                        backgroundColor: `${theme.palette.action.hover} !important`,
                    }
                },
                // Specific styling only for resource name column
                "& .ag-cell-resource-name": {
                    overflow: "hidden !important",
                    padding: "0 !important",
                    "& .ag-cell-wrapper": {
                        overflow: "hidden !important",
                        width: "100% !important",
                    },
                    "& .MuiBox-root": {
                        overflow: "hidden !important",
                        width: "100% !important",
                        maxWidth: "100% !important",
                    },
                    "& .MuiTypography-root": {
                        overflow: "hidden !important",
                        textOverflow: "ellipsis !important",
                        whiteSpace: "nowrap !important",
                        minWidth: 0,
                        maxWidth: "100% !important",
                    }
                },
                "& .ag-header-cell.checkbox-header": {
                    paddingLeft: "12px"
                },
                "& .ag-cell.checkbox-cell": {
                    paddingLeft: "12px"
                },
                "& .ag-row-hover": {
                    backgroundColor: `${theme.palette.action.hover} !important`,
                },
                "& .ag-row-focus": {
                    backgroundColor: `${theme.palette.action.selected} !important`,
                },
                "& .ag-row-selected": {
                    backgroundColor: `${theme.palette.action.selected} !important`,
                },
                "& .ag-row-no-highlight": {
                    "&:hover": {
                        backgroundColor: `${theme.palette.action.hover} !important`,
                    },
                    "&.ag-row-selected": {
                        backgroundColor: `${theme.palette.action.selected} !important`,
                    }
                },
                "& .ag-checkbox-input-wrapper": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                },
                "& .ag-checkbox-input-wrapper input[type='checkbox']": {
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                },
                "& .ag-checkbox-input-wrapper.ag-checked": {
                    color: theme.palette.primary.main, // Changed to black color
                },
                "& .ag-checkbox-input-wrapper.ag-checked::after": {
                    color: theme.palette.primary.main, // Apply black color
                },
                // Add shadow on hovered column header
                "& .ag-header-cell:hover": {
                    boxShadow: theme.palette.mode === "dark"
                        ? "0 2px 8px 0 rgba(0,0,0,0.6)"
                        : "0 2px 8px 0 rgba(0,0,0,0.15)",
                    zIndex: 2,
                    position: "relative",
                }
            }}
        >
            {isLoading ? (
                <SkeletonLoader count={15} styles={{ height: 20 }} />
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 0,
                            width: "100%",
                            height: `${headerAreaHeight}px`,
                        }}
                    >
                        {/* Search Input */}
                        {showSearch && (
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    p: 1,
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    size="small"
                                    variant="standard" // Removes the border
                                    fullWidth
                                    placeholder={searchPlaceholder ? searchPlaceholder : "Search"}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setShouldTriggerServerSearch(true);
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchText && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchText("")}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        disableUnderline: true,
                                    }}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        borderRadius: "6px",
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                                        backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.24)",
                                        },
                                        "&:focus-within": {
                                            borderColor: isDarkMode ? theme.palette.primary.main : "",
                                            boxShadow: isDarkMode? `0 0 0 2px ${theme.palette.primary.main}25`:"",
                                            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                                        },
                                        "& .MuiInputBase-root": {
                                            height: "32px !important",
                                            minHeight: "32px !important",
                                            alignItems: "center",
                                            paddingLeft: "8px",
                                            paddingRight: "8px",
                                        },
                                        "& .MuiInputBase-input": {
                                            color: theme.palette.text.primary,
                                            lineHeight: "32px",
                                            padding: "0px !important",
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                            color: theme.palette.text.secondary,
                                            opacity: 0.8,
                                        },
                                        "& fieldset": { border: "none" },
                                    }}
                                />
                            </Box>
                        )}
                        {headerActions}

                        {showAppsIcon && (
                            <Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    sx={{ "&:focus": { outline: "none" } }}
                                >
                                    <AppsIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>


                    {filteredData.length === 0 ? (
                        <Box sx={{
                            height: headerAreaHeight 
                                ? `calc(100% - ${headerAreaHeight}px)` 
                                : "100%",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <NoRowsOverlay hasActiveAgent={hasActiveAgent} onAddRuleClick={onAddRuleClick} message={noRowsMessage} />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                position: "relative",
                                width: width ? width : "100%",
                                height: headerAreaHeight ? `calc(100% - ${headerAreaHeight}px)` : "100%",
                                ...agGridInlineStyles,
                                "& .ag-header": {
                                    backgroundColor: isDarkMode ? "#393E4B" : "#E7E6F3",
                                    color: theme.palette.text.primary,
                                    fontWeight: "bold",
                                    border: "none",
                                },
                                "& .ag-header-cell": {
                                    border: "none !important",
                                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: isDarkMode ? "#393E4B !important" : `${theme.palette.action.hover} !important`,

                                    },
                                },
                                // Hide the sort order numbers (1, 2, etc.) in column headers - keeps arrows visible
                                "& .ag-sort-order": {
                                    display: "none !important",
                                },
                                "& .ag-paging-panel": {
                                    backgroundColor: isDarkMode ? "#393E4B" : "#FFFFFF",
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    pb: 0,
                                    mb: 0,
                                    marginBottom: 0,
                                    minHeight: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'nowrap',
                                    fontSize: '13px',
                                    gap: 1,
                                    padding: '0 16px',
                                    borderTop: `1px solid ${isDarkMode ? "#2F3138" : "#E4E7EC"}`,
                                    '& > *': {
                                        margin: '0 4px',
                                        padding: 0,
                                        lineHeight: 1.2,
                                    },
                                    '& .ag-paging-row-summary-panel': {
                                        margin: 0,
                                        padding: 0,
                                        fontSize: '13px',
                                        fontWeight: 400,
                                    },
                                    '& .ag-paging-page-summary-panel': {
                                        margin: 0,
                                        padding: 0,
                                        fontSize: '13px',
                                        fontWeight: 400,
                                    },
                                    '& .ag-paging-button': {
                                        // minWidth: 24,
                                        // minHeight: 24,
                                        padding: 0,
                                        margin: '0 2px',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                    },
                                    '& select': {
                                        minHeight: 24,
                                        fontSize: '13px',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                    },
                                },
                                "& .ag-root-wrapper": {
                                    border: "none",
                                },
                                "& .ag-row": {
                                    border: "none !important",
                                },
                                "& .ag-cell": {
                                    border: "none !important",
                                },
                                "& .ag-header-row": {
                                    border: "none !important",
                                },
                                "& .ag-checkbox-input-wrapper": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                                "& .ag-checkbox-input-wrapper .ag-icon": {
                                    color: isDarkMode ? "rgba(255, 255, 255, 0.9) !important" : undefined,
                                },
                                "& .ag-checkbox-input-wrapper.ag-checked .ag-icon, & .ag-checkbox-input-wrapper.ag-checked::after": {
                                    color: isDarkMode ? "#3182ce !important" : theme.palette.primary.main,
                                },
                                "& .ag-header-cell.checkbox-header": {
                                    paddingLeft: "12px",
                                    "& .ag-checkbox-input-wrapper": {
                                        marginLeft: "4px",
                                    }
                                },
                                "& .ag-cell.checkbox-cell": {
                                    paddingLeft: "12px",
                                    "& .ag-checkbox-input-wrapper": {
                                        marginLeft: "4px",
                                    }
                                },
                                //ag-grid scroll
                                "& .ag-body-vertical-scroll-viewport": {
                                    "&::-webkit-scrollbar": {
                                        width: "4px !important",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: isDarkMode ? "#424242" : "#D3D3D3",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                        backgroundColor: "#B0B0B0",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: isDarkMode ? "#191C24" : "#f0f0f0",
                                        //backgroundColor: "red",
                                        borderRadius: "10px",
                                    },
                                },
                                "& .ag-body-horizontal-scroll-viewport": {
                                    "&::-webkit-scrollbar": {
                                        height: "4px !important",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: isDarkMode ? "#424242" : "#D3D3D3",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                        backgroundColor: "#B0B0B0",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: isDarkMode ? "#191C24" : "#f0f0f0",
                                        borderRadius: "10px",
                                    },
                                },
                            }}
                        >
                            <div
                                className={`ag-theme-material ${theme.palette.mode === 'dark' ? 'dark-theme' : ''}`}
                                style={{
                                    height: "100%",
                                    width: width ? width : "100%",
                                }}
                            >
                                <AgGridReact
                                    // Force re-render when theme changes
                                    theme="legacy"
                                    className="ag-theme-material"
                                    ref={gridRef}

                                    rowData={filteredData}
                                    columnDefs={processedColumnDefs}
                                    rowModelType="clientSide"
                                    gridOptions={mergedGridOptions}
                                />
                            </div>
                        </Box>
                    )}
                </>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: {
                        boxShadow: theme.palette.mode === "dark"
                            ? "0px 4px 20px rgba(0, 0, 0, 0.3)"
                            : "0px 4px 20px rgba(0, 0, 0, 0.1)",
                        borderRadius: 1,
                        minWidth: 200
                    }
                }}
            >
                <Typography variant="h6" sx={{ padding: 1, pb: 0 }}>
                    Columns
                </Typography>
                {columnDefs.map(
                    (col, index) =>
                        col?.headerName &&
                        col.field !== "id" && (
                            <MenuItem key={col.field}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={col?.field ? visibleColumns.includes(col.field) : false}
                                            onChange={() => col?.field && handleColumnToggle(col.field)}
                                            disabled={
                                                index === 0 ||
                                                col.field === 'number' ||
                                                (col.headerName ? mandatoryColumns.includes(col.headerName) : false)
                                            }
                                        />
                                    }
                                    disabled={col.pinned == 'left'}
                                    label={col.headerName}
                                />
                            </MenuItem>
                        )
                )}
            </Menu>
        </Box>
    );
};

export default AgGridTable;
